/**
 * Evenly-spaced x-axis tick labels for an INDEX-based ("point") scale.
 *
 * Our financial line charts use a Nivo `point` x-scale, which gives every data
 * point an equal pixel slot regardless of its date. The ONLY way to guarantee
 * evenly-spaced (never-overlapping) axis labels on any device width is therefore
 * to pick data points at equal INDEX intervals.
 *
 * We deliberately do NOT snap to calendar boundaries (1st of month/quarter/etc).
 * Snapping makes the spacing depend on data density, so on gappy or non-uniform
 * series the labels cluster and overlap — even spacing wins over round dates.
 *
 * Returns the first and last points plus equally-spaced points between, capped
 * to a device-appropriate count. The count adapts to width; the spacing is
 * always even.
 *
 * Also right for a LINEAR time scale over trading days (the Insiders chart):
 * sessions are close enough to uniform that equal index steps land at near-equal
 * pixels, and the ends are always labelled.
 */
export const evenlySpacedTickValues = <T extends string | number>(
  xValues: T[],
  isMobile: boolean,
  /** Overrides the device count, for a chart narrower than its screen. */
  maxCount?: number
): T[] => {
  const n = xValues.length;
  if (n === 0) return [];

  const targetCount = Math.min(n, maxCount ?? (isMobile ? 5 : 8));
  if (targetCount <= 1) return [xValues[0]];

  const ticks: T[] = [];
  for (let i = 0; i < targetCount; i++) {
    ticks.push(xValues[Math.round((i * (n - 1)) / (targetCount - 1))]);
  }

  // Rounding can repeat an index on very short series; dedupe, preserving order.
  return Array.from(new Set(ticks));
};

/**
 * Evenly-spaced x-axis tick labels for an intraday TIME series, placed on a
 * single consistent clock grid (e.g. every hour on the :00) so the labels are
 * BOTH evenly spaced and read as round times.
 *
 * Why not "snap each evenly-indexed tick to the nearest :30"? Independent
 * rounding shares no interval: two ticks ~55 min apart can round toward each
 * other (one up, one down) and collapse to a 30-min gap, so the labels bunch
 * (15:30 · 16:30 · 17:30 · 18:00 …). The cure is one interval and one phase.
 *
 * We pick the smallest "nice" interval whose marks across the span don't exceed
 * the target label count, then emit marks at exact multiples of it. On a Nivo
 * `point` scale, even pixel spacing means even INDEX spacing, which equals even
 * TIME spacing only when the series is ~uniformly sampled — true for regular-
 * hours intraday data — so an even time grid renders as even pixels. Each grid
 * time is mapped to its nearest real data point (the scale can only tick on
 * actual x-values); on dense 1-minute data that point sits exactly on the
 * boundary, giving clean round labels.
 *
 * Minute-of-day is unwrapped to a monotonic sequence first, so the grid stays
 * correct even when the session crosses local midnight (e.g. US hours viewed
 * from Asia). Every candidate interval divides 1440, so a multiple of the
 * interval always lands on the clock boundary.
 *
 * @param xValues      ordered x identifiers, as used by the chart's point scale
 * @param minutesOfDay maps an x value to its displayed minutes-since-midnight
 * @param isMobile     fewer labels on small screens
 */
export const evenlySpacedTimeTickValues = (
  xValues: string[],
  minutesOfDay: (x: string) => number,
  isMobile: boolean
): string[] => {
  const n = xValues.length;
  if (n === 0) return [];
  if (n === 1) return [xValues[0]];

  // Unwrap minute-of-day into a monotonic series (clock-of-day === value % 1440)
  // so spans and "nice" multiples are correct across a local-midnight crossing.
  const rawMinuteOfDay = xValues.map(minutesOfDay);
  const minutes: number[] = [rawMinuteOfDay[0]];
  for (let i = 1; i < n; i++) {
    let value = rawMinuteOfDay[i];
    while (value < minutes[i - 1]) value += 1440;
    minutes.push(value);
  }

  const firstMin = minutes[0];
  const lastMin = minutes[n - 1];
  if (lastMin - firstMin <= 0) return [xValues[0], xValues[n - 1]];

  const targetCount = isMobile ? 5 : 8;

  // Smallest interval (all divide 1440 → multiples land on the clock) whose grid
  // marks fit within the target count: the most labels we can show evenly.
  const NICE_INTERVALS = [30, 60, 90, 120, 180, 240, 360, 720];
  let interval = NICE_INTERVALS[NICE_INTERVALS.length - 1];
  for (const candidate of NICE_INTERVALS) {
    const firstMark = Math.ceil(firstMin / candidate) * candidate;
    const markCount = Math.floor((lastMin - firstMark) / candidate) + 1;
    if (markCount <= targetCount) {
      interval = candidate;
      break;
    }
  }

  // Emit each grid mark as its nearest real data point; dedupe (sparse/illiquid
  // series can map two marks to the same point), preserving order.
  const firstMark = Math.ceil(firstMin / interval) * interval;
  const seen = new Set<number>();
  const result: string[] = [];
  for (let mark = firstMark; mark <= lastMin; mark += interval) {
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < n; i++) {
      const diff = Math.abs(minutes[i] - mark);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    if (!seen.has(bestIdx)) {
      seen.add(bestIdx);
      result.push(xValues[bestIdx]);
    }
  }

  return result.length > 0 ? result : [xValues[0], xValues[n - 1]];
};
